import type {
  NetworkDashboardCategoryPdm,
  NetworkDashboardCohortUnit,
  NetworkDashboardStoreAgeFilter,
} from '@gozoki/api-types'
import type { DateRangePreset } from '~/components/ui/date-range-picker'

/** Same shape as `DateRangePicker` value (`from: null` = unbounded past for « Toujours »). */
export interface NetworkDashboardDateRange {
  from: Date | null
  to: Date
}

export interface NetworkDashboardCohortSelection {
  enabled: boolean
  unit: NetworkDashboardCohortUnit
  value: number
}

export type NetworkDashboardComparableSelectionMode = 'auto' | 'custom'

/**
 * Start date sent to the API when the user picks « Toujours » (picker uses `from: null`).
 * Must be on or before any `network_daily_kpis.date` you need included.
 */
export const DASHBOARD_ALL_TIME_START_ISO = '2020-01-01'

/**
 * Plafond (mois calendaires) de la période du dashboard réseau, aligné sur la validation de
 * `GET /stores/get-network-dashboard-kpis` — une plage plus large sature le CPU du backend
 * (incident prod du 01/09/2026 sur une année complète). Le dashboard RH n'est pas concerné.
 */
export const NETWORK_DASHBOARD_MAX_RANGE_MONTHS = 3

/** One selectable metric (CA, UC, …) shown in the left « Indicateur » column. */
export interface DashboardFilterIndicatorOption {
  id: string
  label: string
}

export interface DashboardFiltersRowSelection {
  storeIds: number[]
  storeAgeFilter: NetworkDashboardStoreAgeFilter
  /**
   * Explicit age-selection store ids (partial custom). Omitted when the preset is exactly
   * default / all / mature / young without a CSV list.
   */
  ageStoreIds?: number[]
  dateRange: NetworkDashboardDateRange
  cohort?: NetworkDashboardCohortSelection
  /** Omitted ⇒ dialog defaults to `'7d'` for the picker highlight. */
  datePreset?: DateRangePreset | null
}

/** Dashboard row only, or first row when comparing. */
export type DashboardFiltersComparisonMode = 'none' | 'dual'

/** Result of « Appliquer » — callers branch on `comparisonMode`. */
export type DashboardFiltersApplyPayload =
  | {
      comparisonMode: 'none'
      indicatorId?: string
      categoryIds?: number[]
      storeIds: number[]
      storeAgeFilter: NetworkDashboardStoreAgeFilter
      ageStoreIds?: number[]
      dateRange: NetworkDashboardDateRange
      datePreset?: DateRangePreset | null
      cohort?: NetworkDashboardCohortSelection
    }
  | {
      comparisonMode: 'dual'
      comparableSelectionMode: NetworkDashboardComparableSelectionMode
      principal: {
        indicatorId?: string
        categoryIds?: number[]
        storeIds: number[]
        storeAgeFilter: NetworkDashboardStoreAgeFilter
        ageStoreIds?: number[]
        dateRange: NetworkDashboardDateRange
        datePreset?: DateRangePreset | null
        cohort?: NetworkDashboardCohortSelection
      }
      comparable: {
        indicatorId?: string
        categoryIds?: number[]
        storeIds: number[]
        storeAgeFilter: NetworkDashboardStoreAgeFilter
        ageStoreIds?: number[]
        dateRange: NetworkDashboardDateRange
        datePreset?: DateRangePreset | null
        cohort?: NetworkDashboardCohortSelection
      }
    }

export interface DashboardFilterIndicatorColumnConfig {
  options: DashboardFilterIndicatorOption[]
  defaultSelectedId?: string
}

export interface DashboardFilterCategoryColumnConfig {
  options: NetworkDashboardCategoryPdm[]
  defaultSelectedIds?: number[]
}
